import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { IOCLookupResponse, IOCType, IOCTypesResponse } from '../types/ioc.types';

@Injectable({ providedIn: 'root' })
export class IOCService {
    // Use proxied endpoint to avoid CORS issues
    private readonly API_URL = '/blackbox';
    private readonly API_KEY = 'sk_live_6ba26e6d32806ce80422198586f15d9bc8b4fac056780ed63e3ede22814c0809';

    // Cache for lookup results (key: IP/domain/etc, value: Observable)
    private cache = new Map<string, Observable<IOCLookupResponse>>();
    private cacheTimeout = 5 * 60 * 1000; // 5 minutes

    constructor(private http: HttpClient) { }

    /**
     * Get HTTP headers with API key
     */
    private getHeaders(): HttpHeaders {
        return new HttpHeaders({
            'X-API-Key': this.API_KEY
        });
    }

    /**
     * Lookup IOC information for a specific value (IP, domain, URL, hash)
     * @param value The value to lookup (e.g., '83.36.44.36')
     * @returns Observable with IOC lookup response
     */
    lookupIOC(value: string): Observable<IOCLookupResponse> {
        console.log('🔍 IOCService: Looking up IOC for:', value);

        // Check cache first
        if (this.cache.has(value)) {
            console.log('📦 IOCService: Returning cached result for:', value);
            return this.cache.get(value)!;
        }

        const url = `${this.API_URL}/iocs/lookup/${encodeURIComponent(value)}`;

        const request$ = this.http.get<IOCLookupResponse>(url, { headers: this.getHeaders() }).pipe(
            tap(response => {
                console.log('✅ IOCService: Received IOC data:', response);
                console.log('✅ IOCService: Total IOCs found:', response.total);
                console.log('✅ IOCService: IOCs array:', response.iocs);
            }),
            catchError(error => {
                console.error('❌ IOCService: Error looking up IOC:', error);
                console.error('❌ IOCService: Error status:', error.status);
                console.error('❌ IOCService: Error message:', error.message);
                console.error('❌ IOCService: Full error object:', JSON.stringify(error, null, 2));
                
                // Determine if API key is expired
                const isApiKeyExpired = error.status === 401;
                
                // Return empty result with error information
                return of({ 
                    total: 0, 
                    iocs: [],
                    error: {
                        status: error.status || 0,
                        message: error.message || 'Error desconocido al consultar amenazas',
                        isApiKeyExpired
                    }
                });
            }),
            shareReplay(1) // Share the result among multiple subscribers
        );

        // Store in cache
        this.cache.set(value, request$);

        // Clear from cache after timeout
        setTimeout(() => {
            this.cache.delete(value);
            console.log('🗑️ IOCService: Cache expired for:', value);
        }, this.cacheTimeout);

        return request$;
    }

    /**
     * Lookup IP address specifically
     * @param ip IP address to lookup
     */
    lookupIP(ip: string): Observable<IOCLookupResponse> {
        return this.lookupIOC(ip);
    }

    /**
     * Lookup domain
     * @param domain Domain to lookup
     */
    lookupDomain(domain: string): Observable<IOCLookupResponse> {
        return this.lookupIOC(domain);
    }

    /**
     * Lookup URL
     * @param url URL to lookup
     */
    lookupURL(url: string): Observable<IOCLookupResponse> {
        return this.lookupIOC(url);
    }

    /**
     * Get available IOC types
     */
    getIOCTypes(): Observable<IOCTypesResponse> {
        const url = `${this.API_URL}/iocs/types`;

        return this.http.get<IOCTypesResponse>(url, { headers: this.getHeaders() }).pipe(
            tap(response => {
                console.log('✅ IOCService: Available IOC types:', response);
            }),
            catchError(error => {
                console.error('❌ IOCService: Error getting IOC types:', error);
                // Return default types on error
                return of({ types: ['domain', 'ip', 'url', 'hash_md5', 'hash_sha1', 'hash_sha256'] as IOCType[] });
            })
        );
    }

    /**
     * Clear all cached results
     */
    clearCache(): void {
        this.cache.clear();
        console.log('🗑️ IOCService: Cache cleared');
    }

    /**
     * Validate if a string is a valid IP address
     */
    isValidIP(value: string): boolean {
        const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
        if (!ipv4Regex.test(value)) return false;

        const parts = value.split('.');
        return parts.every(part => {
            const num = parseInt(part, 10);
            return num >= 0 && num <= 255;
        });
    }

    /**
     * Validate if a string is a valid domain
     */
    isValidDomain(value: string): boolean {
        const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        return domainRegex.test(value);
    }
}
